// Exchange rate data
// In a production environment, these would be fetched from an API
// These are placeholder rates for demonstration purposes
export const EXCHANGE_RATES = {
  HTG: 130.5, // Haitian Gourde
  DOP: 58.75, // Dominican Peso
  EUR: 0.93, // Euro
  CAD: 1.37, // Canadian Dollar
  JMD: 157.38, // Jamaican Dollar
};

export type CurrencyCode = keyof typeof EXCHANGE_RATES | 'USD';

export const CURRENCY_NAMES = {
  USD: 'US Dollar',
  HTG: 'Gourde Haïtienne',
  DOP: 'Peso Dominicain',
  EUR: 'Euro',
  CAD: 'Dollar Canadien',
  JMD: 'Dollar Jamaïcain',
};

const CURRENCY_SYMBOLS = {
  USD: '$',
  HTG: 'G',
  DOP: 'RD$',
  EUR: '€',
  CAD: 'C$',
  JMD: 'J$',
};

/**
 * Format a number as currency
 * @param amount The amount to format
 * @param currencyCode The currency code
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currencyCode: CurrencyCode): string {
  if (currencyCode === 'USD') {
    return `$${amount.toFixed(2)} USD`;
  }
  
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  
  // For HTG and some other currencies, we typically don't use decimal places
  if (currencyCode === 'HTG' || currencyCode === 'JMD') {
    return `${CURRENCY_SYMBOLS[currencyCode]}${Math.round(amount)} ${currencyCode}`;
  }
  
  return `${CURRENCY_SYMBOLS[currencyCode]}${formatter.format(amount)} ${currencyCode}`;
}

/**
 * Convert an amount from USD to another currency
 * @param amountUSD The amount in USD
 * @param toCurrency The target currency code
 * @returns The converted amount
 */
export function convertCurrency(amountUSD: number, toCurrency: CurrencyCode): number {
  if (toCurrency === 'USD') {
    return amountUSD;
  }
  
  const rate = EXCHANGE_RATES[toCurrency as keyof typeof EXCHANGE_RATES];
  return amountUSD * rate;
}

/**
 * Get the exchange rate between USD and another currency
 * @param currencyCode The target currency code
 * @returns The exchange rate string (e.g., "1 USD = 130.5 HTG")
 */
export function getExchangeRate(currencyCode: CurrencyCode): string {
  if (currencyCode === 'USD') {
    return '1 USD = 1 USD';
  }
  
  const rate = EXCHANGE_RATES[currencyCode as keyof typeof EXCHANGE_RATES];
  
  if (currencyCode === 'HTG' || currencyCode === 'JMD') {
    return `1 USD = ${Math.round(rate)} ${currencyCode}`;
  }
  
  return `1 USD = ${rate.toFixed(2)} ${currencyCode}`;
}